import React, {useEffect} from "react";
import {ButtonType, UiButton} from "../ui-components/button/uiButton";
import {useTranslation} from "react-i18next";
import {FriendApi} from "../../services/api/FriendApi";
import {User} from "../../models/User";
import {UiInput} from "../ui-components/input/uiInput";
import {FriendRequests} from "./sections/FriendRequests";
import {SearchResults} from "./sections/SearchResults";
import {UserApi} from "../../services/api/UserApi";
import {ToastType} from "../ui-components/toast/UiToast";
import {FriendsList} from "./sections/FriendsList";
import {AppContext} from "../authProvider/AuthProvider";


const pageMod = {
    friends: "friends",
    friendRequests: "friendRequests",
    search: "search"
}


export const Players: React.FC = () => {
    
    const { t } = useTranslation();
    const {openToastWithParams, errorToast, isConnected} = React.useContext(AppContext);
    
    const [mod, setMod] = React.useState(pageMod.friends);

    const [searchResult, setSearchResult] = React.useState<User[]>([]);
    const [usersWithPendingRequest, setUsersWithPendingRequest] = React.useState<User[]>([]);
    const [friends, setFriends] = React.useState<User[]>([]);
    const [usersSentYouARequest, setUsersSentYouARequest] = React.useState<User[]>([]);
    const [search, setSearch] = React.useState("");
    
    const [title, setTitle] = React.useState("");

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        
        const page = params.get("page");
        if (page !== null && page in pageMod) {
            let param = {};
            if (page === pageMod.search) {
                param = {count: 0}
            }
            
            changeMod(page, param);
        }

        changeMod(mod)
    }, []);
    
    function fetchUsersData() {
        if (!isConnected) return;
        
        FriendApi.getFriends()
            .then(usersFriends => {
                if (usersFriends) {
                    setFriends(usersFriends)
                }
            })
            .catch(() => errorToast());

        FriendApi.getFriendRequests()
            .then(users => {
                if (users) {
                    setUsersSentYouARequest(users)
                }
            })
            .catch(() => errorToast());

        FriendApi.getSentFriendRequests()
            .then(users => {
                if (users) {
                    setUsersWithPendingRequest(users)
                }
            })
            .catch(() => errorToast());
    }
    
    function removeFriend(user: User) {
        FriendApi.removeFriend(user.id)
            .then((ok) => {
                if (ok) {
                    setFriends(prev => prev.filter(u => u.id !== user.id))
                }
            })
            .catch(() => errorToast());
    }

    function sendFriendRequest(user: User) {
        FriendApi.sendFriendRequest(user.id)
            .then((ok) => {
                if (ok) {
                    setUsersWithPendingRequest(prev => [...prev, user]);
                    openToastWithParams({
                        message: t("players.friend-request-sent"),
                        type: ToastType.success
                    });
                } else {
                    openToastWithParams({
                        message: t("error.error-occured"),
                        type: ToastType.error
                    });
                }
            })
            .catch(() => errorToast());
    }
    
    function acceptRequest(user: User) {
        FriendApi.acceptFriendRequest(user.id)
            .then((ok) => {
                if (ok) {
                    setFriends(prev => [...prev, user]);
                    setUsersSentYouARequest(prev => prev.filter(u => u.id !== user.id));
                } else {
                    openToastWithParams({
                        message: t("error.error-occured"),
                        type: ToastType.error
                    });
                }
            })
            .catch(() => errorToast());
    }
    
    function declineRequest(user: User) {
        FriendApi.declineFriendRequest(user.id)
            .then((ok) => {
                if (ok) {
                    setUsersSentYouARequest(prev => prev.filter(u => u.id !== user.id));
                } else {
                    openToastWithParams({
                        message: t("error.error-occured"),
                        type: ToastType.error
                    });
                }
            })
            .catch(() => errorToast());
    }
    
    function searchPlayers(search: string) {
        UserApi.searchUsers(search)
            .then(users => {
                setSearchResult(users);
                changeMod(pageMod.search, {count: users.length});
                setSearch(search);
            })
            .catch(() => errorToast());
    }

    function renderContent(mod: string) {
        switch (mod) {
            case pageMod.friendRequests:
                if (!isConnected) return <></>;
                
                return <FriendRequests
                    acceptRequest={acceptRequest}
                    declineRequest={declineRequest}
                    usersWithPendingRequest={usersSentYouARequest}
                />;
            case pageMod.search:
                return <SearchResults
                    search={search}
                    players={friends}
                    searchResult={searchResult}
                    removeFriend={removeFriend}
                    sendFriendRequest={sendFriendRequest}
                    usersIdWithPendingRequest={usersWithPendingRequest}
                />;
            default:
                if (!isConnected) return <></>;
                
                return <FriendsList
                    friends={friends}
                    removeFriend={removeFriend}
                />;
        }
    }
    
    function changeMod(mod: string, stringParam?: {[key: string]: string}) {
        switch (mod) {
            case pageMod.friends:
                setTitle(t("players.x-friends", {count: friends.length}));
                break;
            case pageMod.friendRequests:
                setTitle(t("players.x-pending-friend-request", {count: usersSentYouARequest.length}))
                break;
            case pageMod.search:
                setTitle(t("global.x-results", stringParam));
        }
        
        setMod(mod);
        fetchUsersData();
    }
    
    return (
        <div className="px-12 md:px-28 xl:px-44 pt-20 pb-12 md:pt-28 xl:pt-30 gap-6 h-full flex flex-col overflow-hidden">
            {mod !== pageMod.friends &&
                <UiButton
                    className="xs:w-fit"
                    text={t("players.my-friends")}
                    icon={"arrow-left"}
                    type={ButtonType.transparentBg}
                    onClick={() => {
                        changeMod(pageMod.friends)
                    }}
                />
            }

            <div className="flex flex-col-reverse xs:flex-row gap-4 justify-between">
                <h1 className="text-4xl">{title}</h1>

                {isConnected && <UiButton
                    className="w-full xs:w-fit h-fit"
                    text={t("players.friend_request", {count: usersSentYouARequest.length})}
                    onClick={() => {
                        changeMod(pageMod.friendRequests)
                    }}
                />}
            </div>

            <div className="flex flex-col gap-12">
                <div className="flex flex-col items-center gap-6">
                    <UiInput
                        className="h-16 w-full"
                        placeholder={t("global.search-player")}
                        icon={"search"}
                        iconFunction={searchPlayers}
                        clearInputAfterIconClick={false}
                        enterKeyTriggerIconFunction={true}
                        enterKeyTriggerBlurInput={true}
                    />

                    <hr className="h-0.5 w-11/12"/>
                </div>

                <div className="h-[calc(100vh-30rem)] overflow-y-auto pr-4">
                    {renderContent(mod)}
                </div>
            </div>
        </div>
    )
}
